import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Steps, Radio, Alert } from 'antd';
import { FileAddOutlined, FileSearchOutlined, FileDoneOutlined } from '@ant-design/icons';
import { scenarioActions } from '../../redux/actions/scenarioActions';
import { accountActions } from '../../redux/actions/accountActions';

import ImportUploader from '../../components/ImportUploader';
import PageHeader from '../../components/PageHeader';
import useReadSpreadsheetFile from '../../hooks/useReadSpreadsheetFile';
import ImportValidation from '../../components/ImportValidation';
import ImportExecution from '../../components/ImportValidation/ImportExecution';
import TemplateImportValidation from '../../components/TemplateImportValidation';
import TemplateImportExecution from '../../components/TemplateImportExecution';
import RenderUpdater from '../../components/RenderUpdater';

const Import = () => {
    const dispatch = useDispatch();
    const [step, setStep] = useState(0);
    const [importType, setImportType] = useState('values');
    const [workbook, onFileUpload] = useReadSpreadsheetFile();

    const [result, setResult] = useState({
        tuples: [],
        accounts: [],
        associatedAccounts: [],
        formulas: [],
        errors: [],
        warnings: [],
    });

    const [finished, setFinished] = useState(null);

    const updatingOrganization = useSelector((state) => state.organization.updatingOrganization);
    const currentScenarioId = useSelector(
        (state) => state.scenarios?.currentScenario?.scenario_id,
    );

   const handleFinished = useCallback(
    async (ok, data) => {
        setFinished(ok);

        if (ok && importType === 'values' && currentScenarioId) {
            await dispatch(
                scenarioActions.getScenario(currentScenarioId, { force: true }),
            );
        }

        if (ok && importType === 'template') {
            const accountStructure = data?.accountStructure;

            if (accountStructure) {
                dispatch({
                    type: 'GET_ACCOUNT_STRUCTURE_SUCCESS',
                    accountStructure,
                });
            }

            if (currentScenarioId && accountStructure) {
                const freshScenario = await dispatch(
                    scenarioActions.getScenario(currentScenarioId, {
                        force: true,
                    }),
                );

                if (freshScenario) {
                    await dispatch(
                        scenarioActions.calculateConvergence(
                            freshScenario,
                            accountStructure,
                            [],
                        ),
                    );
                }
            }
        }
    },
    [dispatch, currentScenarioId, importType],
);
    const isTemplate = importType === 'template';

    const validateStep = [
        () => Boolean(workbook),
        () =>
            isTemplate
                ? Boolean(
                      (result.accounts?.length ||
                          result.associatedAccounts?.length ||
                          result.formulas?.length) &&
                          !result.errors?.length,
                  )
                : Boolean(result.tuples?.length && !result.errors?.length),
        () => Boolean(finished),
    ][step];

    const renderStep = (stepCount) => {
        if (stepCount === 0) {
            return (
                <>
                    <Radio.Group
                        value={importType}
                        onChange={(e) => {
                            setImportType(e.target.value);
                            setStep(0);
                            setResult({
                                tuples: [],
                                accounts: [],
                                associatedAccounts: [],
                                formulas: [],
                                errors: [],
                                warnings: [],
                            });
                            setFinished(null);
                        }}
                        style={{ marginBottom: 16 }}
                    >
                        <Radio.Button value="values">Import Values</Radio.Button>
                        <Radio.Button value="template">Import Account Structure</Radio.Button>
                    </Radio.Group>
                    <ImportUploader onFileUpload={onFileUpload} />
                </>
            );
        }

        if (stepCount === 1) {
            return isTemplate ? (
                <TemplateImportValidation workbook={workbook} onResult={setResult} />
            ) : (
                <ImportValidation workbook={workbook} onResult={setResult} />
            );
        }

        return isTemplate ? (
            <TemplateImportExecution
                accounts={result.accounts}
                associatedAccounts={result.associatedAccounts}
                formulas={result.formulas}
                onResult={handleFinished}
            />
        ) : (
            <ImportExecution tuples={result.tuples} onResult={handleFinished} />
        );
    };

    const goToPreviousStep = () => setStep((c) => c - 1);
    const goToNextStep = () => setStep((c) => c + 1);

    const renderContent = () => {
        if (updatingOrganization) return <RenderUpdater />;

        return (
            <>
                <PageHeader />
                <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ padding: 20 }}>
                        <Steps current={step}>
                            <Steps.Step title="Upload" icon={<FileAddOutlined />} description="Upload a file" />
                            <Steps.Step title="Verify" icon={<FileSearchOutlined />} description="Verify the file" />
                            <Steps.Step title="Execute" icon={<FileDoneOutlined />} description="Import and Process" />
                        </Steps>
                    </div>

                    <Divider style={{ margin: 0, borderTopColor: 'rgba(0, 0, 0, 0.1)' }} />

                    <div style={{ height: '50vh', overflow: 'auto' }}>{renderStep(step)}</div>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 30 }}>
                        {!finished ? (
                            <>
                                <Button type="primary" onClick={goToPreviousStep} disabled={step < 1}>
                                    Back
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={goToNextStep}
                                    disabled={step > 1 || !validateStep()}
                                >
                                    {step < 2 ? 'Next' : 'Finish Import'}
                                </Button>
                            </>
                        ) : null}
                    </div>
                </div>
            </>
        );
    };

    return renderContent();
};

export default Import;